import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { colors } from "../../styles/colors";

const MilestoneSidebar = ({
  dream,
  goalsByDream,
  milestonesByGoal,
  draftDates,
  dragId,
  onDragStart,
  onDragEnd,
  onRemove,
  onOpenMilestone,
}) => {
  const [openGoalIds, setOpenGoalIds] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<"unassigned" | "all">("unassigned");
  const goals = (dream ? goalsByDream[dream.id] || [] : []);

  useEffect(() => {
    if (goals.length > 0) {
      const initial: Record<string, boolean> = {};
      goals.forEach((g, i) => { initial[g.id] = i === 0; });
      setOpenGoalIds(initial);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dream?.id]);

  if (!dream || goals.length === 0) {
    return (
      <div className="w-full lg:w-[272px] lg:flex-shrink-0">
        {dream && (
          <p style={{ color: colors.textTertiary, fontSize: 13, padding: "8px 4px", margin: 0 }}>
            No goals for this dream yet.
          </p>
        )}
      </div>
    );
  }

  const unassignedCount = goals.reduce((count, goal) => {
    const allMs = milestonesByGoal[goal.id] || [];
    return count + allMs.filter((m) => {
      if ((m as any).completed) return false;
      const draftDate = draftDates[m.id];
      const effectiveDate = draftDate !== undefined ? draftDate : m.date || "";
      return !effectiveDate;
    }).length;
  }, 0);

  const allCount = goals.reduce((count, goal) => {
    const allMs = milestonesByGoal[goal.id] || [];
    return count + allMs.filter((m) => !(m as any).completed).length;
  }, 0);

  const sortedGoals = [...goals].sort((a, b) => {
    const aMs = milestonesByGoal[a.id] || [];
    const bMs = milestonesByGoal[b.id] || [];
    const aDone = aMs.length > 0 && aMs.every((m) => !!(m as any).completed);
    const bDone = bMs.length > 0 && bMs.every((m) => !!(m as any).completed);
    if (aDone === bDone) return 0;
    return aDone ? 1 : -1;
  });

  return (
    <div
      className="w-full lg:w-[272px] lg:flex-shrink-0"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        paddingBottom: 16,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {([
          { key: "unassigned", label: `Unassigned (${unassignedCount})` },
          { key: "all", label: `All (${allCount})` },
        ] as const).map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                borderRadius: 999,
                border: isActive ? `1px solid ${colors.brandPrimary}` : `1px solid ${colors.borderLight}`,
                background: isActive ? colors.brandPrimary : "#ffffff",
                color: isActive ? "#ffffff" : colors.textPrimary,
                padding: "10px 12px",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {sortedGoals.map((goal) => {
        const allMs = milestonesByGoal[goal.id] || [];
        const completedMilestones = allMs.filter((m) => !!(m as any).completed);
        const goalAllDone = completedMilestones.length > 0 && completedMilestones.length === allMs.length;
        const milestones = allMs.filter((m) => {
          if ((m as any).completed) return false;
          if (activeTab === "all") return true;
          const draftDate = draftDates[m.id];
          const effectiveDate = draftDate !== undefined ? draftDate : m.date || "";
          return !effectiveDate;
        });
        const isOpen = !!openGoalIds[goal.id];
        const totalGoalMilestones = allMs.length;
        const plannedCount = allMs.filter((m) => {
          if ((m as any).completed) return false;
          const d = draftDates[m.id];
          return d !== undefined ? !!d : !!(m as any).date;
        }).length;

        return (
          <section
            key={goal.id}
            style={{
              borderRadius: 16,
              border: `1px solid ${colors.borderLight}`,
              backgroundColor: "#ffffff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}
          >
            <button
              type="button"
              onClick={() => setOpenGoalIds((previous) => ({ ...previous, [goal.id]: !isOpen }))}
              style={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 16px",
                textAlign: "left",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                borderRadius: isOpen ? "16px 16px 0 0" : 16,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
                  {goalAllDone && (
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: "#22c55e",
                      flexShrink: 0,
                    }}>
                      <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                        <polyline points="1.5,5 4,7.5 8.5,2" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  )}
                  <p
                    title={goal.title}
                    style={{
                      margin: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: goalAllDone ? "#15803d" : colors.textTertiary,
                      textDecoration: goalAllDone ? "line-through" : undefined,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {goal.title}
                  </p>
                </div>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: colors.textTertiary }}>
                  {totalGoalMilestones} milestone{totalGoalMilestones !== 1 ? "s" : ""}
                  {plannedCount > 0 && (
                    <span style={{ color: colors.brandPrimary }}> · {plannedCount} planned</span>
                  )}
                </p>
              </div>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                style={{
                  flexShrink: 0,
                  color: colors.textTertiary,
                  transition: "transform 0.2s",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <path
                  d="M3 5l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isOpen && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  borderTop: `1px solid ${colors.borderLight}`,
                  background: "rgba(0,0,0,0.015)",
                  padding: 12,
                  borderRadius: "0 0 16px 16px",
                }}
              >
                {goalAllDone && completedMilestones.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {completedMilestones.map((milestone) => (
                      <div key={milestone.id} style={{ display: "flex", flexDirection: "column", gap: 8, padding: 10, borderRadius: 10, background: "rgba(0,0,0,0.02)", border: `1px solid ${colors.borderLight}` }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <p title={milestone.title} style={{ margin: 0, fontSize: 13, fontWeight: 600, color: colors.textPrimary, textDecoration: "line-through", flex: 1, minWidth: 0 }}>
                            {milestone.title}
                          </p>
                          <button
                            type="button"
                            onClick={() => onOpenMilestone?.({
                              iso: milestone.date || "",
                              dream,
                              goal,
                              milestone,
                            })}
                            title="View milestone details"
                            style={{
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              color: colors.brandPrimary,
                              fontSize: 12,
                              fontWeight: 700,
                              padding: 4,
                              flexShrink: 0,
                            }}
                          >
                            Details
                          </button>
                        </div>
                        <p style={{ margin: 0, fontSize: 11, color: colors.textTertiary, textDecoration: "line-through" }}>
                          {milestone.date ? dayjs(`${milestone.date}T00:00:00`).format("ddd, MMM D") : "No date"}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {milestones.length === 0 && !goalAllDone ? (
                  <div style={{ padding: 12, fontSize: 11, color: colors.textTertiary }}>
                    All milestones for this goal are scheduled or completed.
                  </div>
                ) : milestones.map((milestone) => {
                  const draftDate = draftDates[milestone.id];
                  const effectiveDate =
                    draftDate !== undefined ? draftDate : milestone.date || "";
                  const isScheduled = !!effectiveDate;
                  const isDragging = dragId === milestone.id;
                  const canDrag = activeTab !== "all" && !isScheduled;

                  return (
                    <div
                      key={milestone.id}
                      draggable={canDrag}
                      onDragStart={canDrag ? (event) => onDragStart(event, milestone.id) : undefined}
                      onDragEnd={canDrag ? onDragEnd : undefined}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        borderRadius: activeTab === "all" ? 0 : 12,
                        border: activeTab === "all"
                          ? "none"
                          : isScheduled
                            ? "1px solid rgba(0,136,255,0.25)"
                            : `1px solid ${colors.borderLight}`,
                        backgroundColor: activeTab === "all"
                          ? "transparent"
                          : isScheduled ? "rgba(0,136,255,0.05)" : "#ffffff",
                        padding: activeTab === "all" ? "8px 0" : 12,
                        cursor: canDrag ? "grab" : "default",
                        opacity: isDragging ? 0.4 : 1,
                        transform: isDragging ? "scale(0.97)" : "scale(1)",
                        transition: "all 0.15s",
                        userSelect: "none",
                        boxShadow: activeTab === "all" ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
                        borderBottom: activeTab === "all" ? `1px solid ${colors.borderLight}` : undefined,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <p
                          title={milestone.title}
                          style={{
                            margin: 0,
                            fontSize: 13,
                            fontWeight: 500,
                            color: colors.textPrimary,
                            lineHeight: 1.4,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {milestone.title}
                        </p>
                        <button
                          type="button"
                          onClick={() => onOpenMilestone?.({
                            iso: effectiveDate,
                            dream,
                            goal,
                            milestone,
                          })}
                          title="View milestone details"
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: colors.brandPrimary,
                            fontSize: 12,
                            fontWeight: 700,
                            padding: 4,
                            flexShrink: 0,
                          }}
                        >
                          Details
                        </button>
                      </div>
                      {isScheduled ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            paddingTop: 4,
                            marginTop: 2,
                          }}
                        >
                          <span style={{ fontSize: 11, fontWeight: 600, color: colors.textTertiary }}>
                            {dayjs(`${effectiveDate}T00:00:00`).format("ddd, MMM D")}
                          </span>
                        </div>
                      ) : (
                        activeTab !== "all" && (
                          <div style={{ paddingTop: 5, borderTop: `1px solid ${colors.borderLight}`, marginTop: 2 }}>
                            <span style={{ fontSize: 10, fontWeight: 500, color: colors.textTertiary, letterSpacing: "0.02em" }}>
                              ↑ Drag to schedule
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default MilestoneSidebar;

